import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Chip,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { Person } from "@mui/icons-material";
import { getPlayersRelatedToTeam, saveLineupBulk } from "../config/mainRequest";

// Match statuses where players are considered to have played (mirrors
// MatchLineUp.js / MatchSummary.js). Drives the `playedInMatch` flag.
const PLAYED_IN_STATUSES = [3, 6, 7, 8, 9];

// Same position id → alias mapping as MatchSummary.
const POSITION_LIST = [
  { id: 1, alias: "GK" }, { id: 2, alias: "CB" }, { id: 3, alias: "LB" },
  { id: 4, alias: "RB" }, { id: 5, alias: "LWB" }, { id: 6, alias: "RWB" },
  { id: 7, alias: "CDM" }, { id: 8, alias: "CM" }, { id: 9, alias: "CAM" },
  { id: 10, alias: "LM" }, { id: 11, alias: "RM" }, { id: 12, alias: "LW" },
  { id: 13, alias: "RW" }, { id: 14, alias: "LF" }, { id: 15, alias: "RF" },
  { id: 16, alias: "CF" }, { id: 17, alias: "ST" },
];
const positionAlias = (id) => POSITION_LIST.find((p) => p.id === id)?.alias || "NA";

const notationLabel = (n) =>
  n === 1 ? "Starting XI" : n === 2 ? "Substitute" : "—";

// Parse a possibly-empty string to a non-negative int (or null when blank).
const toInt = (v) => {
  if (v === "" || v == null) return null;
  const n = parseInt(v, 10);
  return isNaN(n) ? null : Math.max(0, n);
};

const isPositiveInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0;
};

const notationSortOrder = (notation) => (notation === 1 ? 0 : notation === 2 ? 1 : 2);

const numFieldSx = {
  width: 72,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    backgroundColor: "var(--surface-2)",
    "& fieldset": { borderColor: "var(--border-1)" },
    "&:hover fieldset": { borderColor: "var(--text-2)" },
  },
  "& input": { textAlign: "center", padding: "6px 8px", color: "var(--text-1)" },
};

const GameTimeTab = ({ matchId, matchInfo, token }) => {
  const [rows, setRows] = useState([]);
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const teamAId = matchInfo?.teamA?.id;
  const teamBId = matchInfo?.teamB?.id;
  const orgId = matchInfo?.organizerId;
  const matchStatus = matchInfo?.status;

  const loadPlayers = useCallback(async () => {
    if (!matchId || !teamAId || !teamBId) return;
    setLoading(true);
    try {
      const playedInMatch = PLAYED_IN_STATUSES.includes(matchStatus);
      const [aRes, bRes] = await Promise.all([
        getPlayersRelatedToTeam({ teamId: teamAId, matchId, organizerId: orgId, playedInMatch, token }),
        getPlayersRelatedToTeam({ teamId: teamBId, matchId, organizerId: orgId, playedInMatch, token }),
      ]);

      const toRows = (list, teamId) =>
        (Array.isArray(list) ? list : [])
          .map((p) => {
            const umd = p.user_match_details || {};
            // Lineup only — starting XI (1) and substitutes (2).
            if (![1, 2].includes(umd.lineup_notation)) return null;
            const name =
              `${p.first_name || ""} ${p.last_name || ""}`.trim() ||
              `#${umd.jersey_number ?? ""}`;
            return {
              id: p.id,
              teamId,
              name,
              photo: p.blob_image_url || null,
              jersey: umd.jersey_number ?? "",
              notation: umd.lineup_notation,
              position: positionAlias(umd.preferred_position),
              umd,
              totalGt: umd.game_time != null ? String(umd.game_time) : "",
              startGt: umd.start_gametime != null ? String(umd.start_gametime) : "",
              endGt: umd.end_gametime != null ? String(umd.end_gametime) : "",
            };
          })
          .filter(Boolean);

      const sortTeamRows = (list) =>
        [...list].sort((a, b) => {
          const notationDiff = notationSortOrder(a.notation) - notationSortOrder(b.notation);
          if (notationDiff !== 0) return notationDiff;
          const jerseyDiff = (Number(a.jersey) || 0) - (Number(b.jersey) || 0);
          if (jerseyDiff !== 0) return jerseyDiff;
          return a.name.localeCompare(b.name);
        });

      setRows([...sortTeamRows(toRows(aRes, teamAId)), ...sortTeamRows(toRows(bRes, teamBId))]);
      setDirtyIds(new Set());
    } catch (err) {
      console.error("[GameTimeTab] fetch failed:", err?.message);
      toast.error("Failed to load players");
    } finally {
      setLoading(false);
    }
  }, [matchId, teamAId, teamBId, orgId, matchStatus, token]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const markDirty = (id) =>
    setDirtyIds((prev) => new Set(prev).add(id));

  // Edit handlers mirror the /matchupdate page exactly:
  //  - total → start = 0, end = total
  //  - start → total = end - start
  //  - end   → total = end - start
  // Negative values are clamped to 0.
  const clampStr = (v) =>
    v === "" ? "" : String(Math.max(0, parseInt(v, 10) || 0));

  const onChangeTotal = (id, value) => {
    const total = clampStr(value);
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, totalGt: total, startGt: "0", endGt: total } : r))
    );
    markDirty(id);
  };

  const onChangeStart = (id, value) => {
    const start = clampStr(value);
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const total = String(Math.max(0, (parseInt(r.endGt, 10) || 0) - (parseInt(start, 10) || 0)));
        return { ...r, startGt: start, totalGt: total };
      })
    );
    markDirty(id);
  };

  const onChangeEnd = (id, value) => {
    const end = clampStr(value);
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const total = String(Math.max(0, (parseInt(end, 10) || 0) - (parseInt(r.startGt, 10) || 0)));
        return { ...r, endGt: end, totalGt: total };
      })
    );
    markDirty(id);
  };

  const buildMapping = (r) => ({
    error: null,
    user_id: r.id,
    match_id: Number(matchId),
    team_id: r.teamId,
    tournament_id: matchInfo?.tournamentId ?? null,
    player_name: r.name,
    jersey_number: r.umd.jersey_number ?? null,
    preferred_position: r.umd.preferred_position ?? null,
    preferred_position_sub_level: r.umd.preferred_position_sub_level ?? null,
    lineup_notation: r.umd.lineup_notation ?? null,
    start_gametime: toInt(r.startGt),
    end_gametime: toInt(r.endGt),
    game_time: toInt(r.totalGt),
    substituted_player_id: r.umd.substituted_player_id ?? null,
    substituted_player_name: r.umd.substituted_player_name ?? null,
  });

  const hasInvalidEndTime = rows.some((r) => dirtyIds.has(r.id) && !isPositiveInt(r.endGt));

  const handleSave = async () => {
    if (dirtyIds.size === 0) return;
    if (hasInvalidEndTime) {
      toast.error("End time must be greater than 0");
      return;
    }
    const mappings = rows.filter((r) => dirtyIds.has(r.id)).map(buildMapping);
    setSaving(true);
    try {
      await saveLineupBulk({ mappings, organizerId: orgId, token });
      toast.success("Game time updated");
      await loadPlayers();
    } catch (err) {
      console.error("[GameTimeTab] save failed:", err?.message);
      toast.error("Failed to update game time");
    } finally {
      setSaving(false);
    }
  };

  const HeaderCell = ({ children, width, align = "left" }) => (
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 700,
        color: "var(--text-2)",
        width,
        flex: width ? "none" : 1,
        textAlign: align,
      }}
    >
      {children}
    </Typography>
  );

  const renderTeamCard = (teamId, teamName) => {
    const teamRows = rows.filter((r) => r.teamId === teamId);
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-1)",
          borderRadius: "12px",
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "var(--text-1)", mb: 1.5 }}>
          {teamName || "Team"}
        </Typography>

        {/* Header row */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 1, py: 0.75, borderRadius: "6px", backgroundColor: "var(--surface-2)" }}
        >
          <HeaderCell width={40}>#JR</HeaderCell>
          <HeaderCell>Player</HeaderCell>
          <Stack direction="row" gap={0.75}>
            <HeaderCell width={72} align="center">Total</HeaderCell>
            <HeaderCell width={72} align="center">Start</HeaderCell>
            <HeaderCell width={72} align="center">End</HeaderCell>
          </Stack>
          <HeaderCell width={56} align="center">Pos</HeaderCell>
        </Stack>

        {teamRows.length === 0 ? (
          <Typography sx={{ fontSize: "12px", color: "var(--text-2)", textAlign: "center", py: 3 }}>
            No lineup players
          </Typography>
        ) : (
          teamRows.map((r, i) => (
            <Stack
              key={r.id}
              direction="row"
              alignItems="center"
              sx={{
                px: 1,
                py: 1,
                borderBottom: i < teamRows.length - 1 ? "1px solid var(--border-1)" : "none",
                backgroundColor: dirtyIds.has(r.id) ? "rgba(232,108,0,0.06)" : "transparent",
                borderRadius: dirtyIds.has(r.id) ? "6px" : 0,
              }}
            >
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "var(--text-1)", width: 40 }}>
                {r.jersey !== "" && r.jersey != null ? `#${r.jersey}` : "NA"}
              </Typography>

              <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
                <Avatar src={r.photo || undefined} sx={{ width: 30, height: 30, backgroundColor: "var(--surface-2)" }}>
                  <Person sx={{ fontSize: 18, color: "var(--text-2)" }} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "var(--text-1)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.name}
                  </Typography>
                  <Typography sx={{ fontSize: "10px", color: "var(--text-2)" }}>
                    {notationLabel(r.notation)}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" gap={0.75}>
                <Box sx={{ width: 72, display: "flex", justifyContent: "center" }}>
                  <TextField
                    type="number"
                    size="small"
                    value={r.totalGt}
                    placeholder="—"
                    onChange={(e) => onChangeTotal(r.id, e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={numFieldSx}
                  />
                </Box>
                <Box sx={{ width: 72, display: "flex", justifyContent: "center" }}>
                  <TextField
                    type="number"
                    size="small"
                    value={r.startGt}
                    placeholder="0"
                    onChange={(e) => onChangeStart(r.id, e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={numFieldSx}
                  />
                </Box>
                <Box sx={{ width: 72, display: "flex", justifyContent: "center" }}>
                  <TextField
                    type="number"
                    size="small"
                    value={r.endGt}
                    placeholder="—"
                    onChange={(e) => onChangeEnd(r.id, e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={numFieldSx}
                  />
                </Box>
              </Stack>

              <Box sx={{ width: 56, display: "flex", justifyContent: "center" }}>
                <Chip
                  label={r.position}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "10px",
                    fontWeight: 700,
                    borderRadius: "6px",
                    backgroundColor: "var(--surface-2)",
                    color: "var(--text-2)",
                    border: "1px solid var(--border-1)",
                  }}
                />
              </Box>
            </Stack>
          ))
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "var(--text-1)" }}>
            Game Time
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
            Edit total, start and end minutes for each lineup player.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          disabled={dirtyIds.size === 0 || saving || hasInvalidEndTime}
          onClick={handleSave}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: 600,
            px: 2,
            backgroundColor: "rgba(232, 108, 0, 1)",
            "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" },
          }}
        >
          {saving ? "Saving…" : `Save Changes${dirtyIds.size ? ` (${dirtyIds.size})` : ""}`}
        </Button>
      </Stack>

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress size={28} sx={{ color: "rgba(232,108,0,1)" }} />
        </Stack>
      ) : (
        <Stack direction={{ xs: "column", lg: "row" }} gap={2} alignItems="stretch">
          {renderTeamCard(teamAId, matchInfo?.teamA?.name)}
          {renderTeamCard(teamBId, matchInfo?.teamB?.name)}
        </Stack>
      )}
    </Box>
  );
};

export default GameTimeTab;
